import {useEffect, useState} from 'react'
import {Listbox, ListboxButton, ListboxOption, ListboxOptions} from '@headlessui/react'
import {usePhoneInput, defaultCountries, parseCountry, FlagImage} from 'react-international-phone'
import {parsePhoneNumberFromString, AsYouType} from 'libphonenumber-js'
import 'react-international-phone/style.css'
import {BOOKING_CONSTRAINTS} from '@/constants/booking'

function detectCountry(value) {
    if (!value || typeof value !== 'string' || !value.startsWith('+')) return null

    const formatter = new AsYouType()
    formatter.input(value)
    const fromFormatter = formatter.getCountry()
    if (fromFormatter) return fromFormatter.toLowerCase()

    const fromParse = parsePhoneNumberFromString(value)?.country
    if (fromParse) return fromParse.toLowerCase()

    if (value.length >= 5 && value.length < 16) {
        const padded = value.padEnd(15, '0')
        const padCountry = parsePhoneNumberFromString(padded)?.country
        if (padCountry) return padCountry.toLowerCase()
    }

    return null
}

function tryResolveDialCode(digits) {
    if (!digits) return null
    for (let len = Math.min(4, digits.length); len >= 1; len--) {
        const prefix = digits.slice(0, len)
        const matches = defaultCountries.filter((c) => parseCountry(c).dialCode === prefix)
        if (matches.length === 0) continue
        if (matches.length === 1) {
            return {country: parseCountry(matches[0]), prefixLen: len}
        }
        if (digits.length >= len + 3) {
            const detected = detectCountry('+' + digits)
            if (detected) {
                const matched = matches.find((c) => parseCountry(c).iso2 === detected)
                if (matched) return {country: parseCountry(matched), prefixLen: len}
            }
        }
        return null
    }
    return null
}

export default function FlagSelectWithNumber({
    value = '',
    onChange,
    defaultCountry = BOOKING_CONSTRAINTS.DEFAULT_COUNTRY,
    readOnly = false,
    hideDropdown = false,
    placeholder,
    className = '',
    inputClassName = '',
}) {
    const initialCountry = detectCountry(value) || defaultCountry
    const {inputValue, country, setCountry, handlePhoneValueChange, inputRef} = usePhoneInput({
        defaultCountry: initialCountry,
        value,
        countries: defaultCountries,
        disableCountryGuess: true,
        disableDialCodePrefill: true,
        disableDialCodeAndPrefix: true,
        disableFormatting: true,
        onChange: (data) => onChange?.(data.phone),
    })

    const [draft, setDraft] = useState(null)

    useEffect(() => {
        if (!value) {
            if (country.iso2 !== defaultCountry) setCountry(defaultCountry)
            setDraft(null)
            return
        }
        const detected = detectCountry(value)
        if (detected && detected !== country.iso2) {
            const detectedEntry = defaultCountries
                .map(parseCountry)
                .find((c) => c.iso2 === detected)
            const dialCode = detectedEntry?.dialCode || ''
            const allDigits = (value || '').replace(/\D/g, '')
            const nationalDigits = allDigits.slice(dialCode.length)
            setCountry(detected)
            if (nationalDigits) setDraft(nationalDigits)
        }
    }, [value])

    const handleChange = (e) => {
        const raw = e.target.value
        if (raw.startsWith('+')) {
            const digitsAfterPlus = raw.slice(1).replace(/\D/g, '')
            const resolved = tryResolveDialCode(digitsAfterPlus)
            if (resolved) {
                const remaining = digitsAfterPlus.slice(resolved.prefixLen)
                setCountry(resolved.country.iso2)
                setDraft(remaining)
                onChange?.('+' + resolved.country.dialCode + remaining)
                return
            }
            setDraft(raw)
            return
        }
        setDraft(null)
        handlePhoneValueChange(e)
    }

    const displayValue = draft !== null ? draft : inputValue

    const row = (button) => (
        <div className="flex items-center">
            {button}
            <input
                ref={inputRef}
                type="tel"
                value={displayValue}
                onChange={handleChange}
                placeholder={placeholder}
                readOnly={readOnly}
                className={`flex-1 min-w-0 px-2 py-3.5 outline-none bg-transparent ${inputClassName}`}
            />
        </div>
    )

    if (hideDropdown) {
        return (
            <div className={`rounded-full w-full bg-transparent border border-(--bg-dark-grey) ${className}`}>
                {row(
                    <div className="flex items-center gap-2 pl-4 pr-2 py-0 shrink-0">
                        <FlagImage iso2={country.iso2} size="32px"/>
                        <span className="text-sm">+{country.dialCode}</span>
                    </div>
                )}
            </div>
        )
    }

    return (
        <Listbox value={country.iso2} onChange={(iso2) => setCountry(iso2)}>
            <div className={`w-full ${className}`}>
                <div className="rounded-full w-full bg-transparent border border-(--bg-dark-grey)">
                    {row(
                        <ListboxButton className="flex items-center gap-2 pl-4 pr-2 py-2 cursor-pointer outline-none shrink-0">
                            <FlagImage iso2={country.iso2} size="32px"/>
                            <span className="text-sm">+{country.dialCode}</span>
                            <svg width="10" height="6" viewBox="0 0 10 6" fill="none" className="opacity-60">
                                <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </ListboxButton>
                    )}
                </div>
                <ListboxOptions
                    static={false}
                    className="w-full max-h-60 overflow-y-auto outline-none rounded-2xl bg-white border border-(--bg-dark-grey) mt-2"
                >
                    {defaultCountries.map((c) => {
                        const p = parseCountry(c)
                        return (
                            <ListboxOption
                                key={p.iso2}
                                value={p.iso2}
                                className="px-4 py-2 flex items-center gap-3 cursor-pointer data-[focus]:bg-(--bg-grey) data-[selected]:font-bold"
                            >
                                <FlagImage iso2={p.iso2} size="20px"/>
                                <span className="flex-1 text-sm truncate">{p.name}</span>
                                <span className="text-sm text-(--text-secondary)">+{p.dialCode}</span>
                            </ListboxOption>
                        )
                    })}
                </ListboxOptions>
            </div>
        </Listbox>
    )
}
